import type { ReactNode } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, Snowflake } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { apiGet } from '@/api/axios-client'
import { paths } from '@/routes/paths'
import { useDeskMeta } from './api'

/** Whether the older Support pages are frozen (Support desk setting). False until the setting says so. */
export function useLegacySupportFrozen(enabled = true): boolean {
  const meta = useDeskMeta(enabled)
  return meta.data?.settings.legacyFrozen === true
}

/**
 * Wraps an older Support page (Operations → Support, the older Support dashboard, Reports → Support)
 * once the Support queue is the one place to work tickets: a note at the top that sends people
 * there (to the same ticket, when the link named one), and the page itself greyed and read-only.
 * The server refuses changes through these pages regardless of what is clicked.
 */
export function LegacyFrozen({ children }: { children: ReactNode }) {
  const { workspaceSlug = '' } = useParams()
  const [params] = useSearchParams()
  const frozen = useLegacySupportFrozen()
  const ticket = params.get('ticket')
  const mapped = useQuery({
    queryKey: ['support-desk', 'by-source', ticket],
    queryFn: () => apiGet<{ case: { id: string; reference: string } | null }>(`support-desk/cases/by-source/${encodeURIComponent(ticket ?? '')}`),
    enabled: frozen && Boolean(ticket),
    staleTime: 5 * 60_000,
  })
  if (!frozen) return <>{children}</>
  const target = mapped.data?.case
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div role="status" className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-brand/30 bg-brand-softer p-4">
        <Snowflake className="size-5 shrink-0 text-brand" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">This page is frozen. Work tickets in the Support queue.</p>
          <p className="text-xs text-text-secondary">It stays here read-only for history and old links. Nothing can be changed from it, and it sends no alerts.</p>
        </div>
        {target ? (
          <Button asChild size="sm">
            <Link to={paths.supportDeskCase(workspaceSlug, target.id)}>Open #{target.reference} in the Support queue <ArrowRight className="size-4" /></Link>
          </Button>
        ) : null}
        <Button asChild size="sm" variant={target ? 'outline' : 'default'}>
          <Link to={paths.supportDesk(workspaceSlug)}>Go to the Support queue <ArrowRight className="size-4" /></Link>
        </Button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col opacity-70 grayscale" aria-label="Frozen page, read-only">{children}</div>
    </div>
  )
}
